import { Button, useMutation, useNotify } from "react-admin";
import SaveIcon from "@material-ui/icons/Save";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { useFormState, useField } from "react-final-form";
import _ from "lodash";
import { Fragment, useEffect, useState } from "react";

interface IUpdateRouteButton {
  routeLocationData: any;
  index: number;
  handleCloseRoutePanel: any;
  isUpdateRoutePlan?: boolean;
}

const useStyles = makeStyles({
  sidePanelSaveRouteButton: {
    backgroundColor: "#4F5B7B",
    boxShadow: "0 4px 8px rgb(79 91 123 / 30%)",
    padding: "8px",
    margin: "12px 8px 0 0",
    float: "right",
    minWidth: "10px",
    "& .MuiButton-textSizeSmall": {
      padding: 0,
    },
    "& .MuiButton-label > .MuiSvgIcon-root": {
      fontSize: 25,
    },
    "&:hover": {
      backgroundColor: "#4F5B7B !important",
    },
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
  },
});

const CustomConfirmButton = ({
  routeLocationData,
  index,
  handleCloseRoutePanel,
  isUpdateRoutePlan,
}: IUpdateRouteButton) => {
  const { values } = useFormState();
  const routeField = useField("route");

  const notify = useNotify();

  const busSchedulePlanId = useField(`id`);
  const routeId = useField(`route.id`);

  const nextRoutePlanRoutesIdField = useField(
    `nextRoutePlan.routes[${index}].id`
  );
  const nextRoutePlanRoutesNameField = useField(
    `nextRoutePlan.routes[${index}].name`
  );
  const nextRoutePlanRoutesBusBayNumberField = useField(
    `nextRoutePlan.routes[${index}].busBayNumber`
  );
  const nextRoutePlanRoutesStartTimeField = useField(
    `nextRoutePlan.routes[${index}].startTime`
  );
  const nextRoutePlanRoutesEndTimeField = useField(
    `nextRoutePlan.routes[${index}].endTime`
  );
  const nextRoutePlanRoutesDriverIdField = useField(
    `nextRoutePlan.routes[${index}].driverId`
  );
  const nextRoutePlanRoutesBusAssistantIdField = useField(
    `nextRoutePlan.routes[${index}].busAssistantId`
  );
  const nextRoutePlanRoutesVehicleIdField = useField(
    `nextRoutePlan.routes[${index}].vehicleId`
  );
  const nextRoutePlanRoutesLocationsField = useField(
    `nextRoutePlan.routes[${index}].routeLocations`
  );
  const nextRoutePlanRoutesBackupDriversField = useField(
    `nextRoutePlan.routes[${index}].backupDriverIds`
  );
  const nextRoutePlanRoutesBackupVehiclesField = useField(
    `nextRoutePlan.routes[${index}].backupVehicleIds`
  );
  const nextRoutePlanRoutesBackupBusAssistantsField = useField(
    `nextRoutePlan.routes[${index}].backupBusAssistantIds`
  );

  const routesNameField = useField(`routes[${index}].name`);
  const routesBusBayNumberField = useField(`routes[${index}].busBayNumber`);
  const routesStartTimeField = useField(`routes[${index}].startTime`);
  const routesEndTimeField = useField(`routes[${index}].endTime`);
  const routesDriverIdField = useField(`routes[${index}].driverId`);
  const routesBusAssistantIdField = useField(`routes[${index}].busAssistantId`);
  const routesVehicleIdField = useField(`routes[${index}].vehicleId`);
  const routesLocationsField = useField(`routes[${index}].routeLocations`);
  const routesBackupDriversField = useField(`routes[${index}].backupDriverIds`);
  const routesBackupVehiclesField = useField(
    `routes[${index}].backupVehicleIds`
  );
  const routesBackupBusAssistantsField = useField(
    `routes[${index}].backupBusAssistantIds`
  );

  const [handleUpdateRoute, { loading }] = useMutation(
    {
      type: "customUpdate",
      resource: `busSchedulePlans/${busSchedulePlanId.input.value}/routes/${routeId.input.value}`,
      payload: {
        data: {
          id: values.route.id,
          name: values.route.name,
          busBayNumber: values.route.busBayNumber,
          startTime: values.route.startTime,
          endTime: values.route.endTime,
          driverId: values.route.driverId,
          busAssistantId: values.route.busAssistantId,
          vehicleId: values.route.vehicleId,
          backupDriverIds: values.route.backupDriverIds
            ? values.route.backupDriverIds
            : values.route.backupDrivers?.length > 0
            ? values.route.backupDrivers.map(
                (backupDriver: any) => backupDriver.id
              )
            : [],
          backupVehicleIds: values.route.backupVehicleIds
            ? values.route.backupVehicleIds
            : values.route.backupVehicles?.length > 0
            ? values.route.backupVehicles.map(
                (backupVehicle: any) => backupVehicle.id
              )
            : [],
          backupBusAssistantIds: values.route.backupBusAssistantIds
            ? values.route.backupBusAssistantIds
            : values.route.backupBusAssistants?.length > 0
            ? values.route.backupBusAssistants.map(
                (backupBA: any) => backupBA.id
              )
            : [],
          routeLocations: values.route.routeLocations,
        },
      },
    },
    {
      onSuccess: ({ data }) => {
        nextRoutePlanRoutesIdField.input.onChange(data.id);
        nextRoutePlanRoutesNameField.input.onChange(values.route.name);
        nextRoutePlanRoutesBusBayNumberField.input.onChange(
          values.route.busBayNumber
        );
        nextRoutePlanRoutesStartTimeField.input.onChange(
          values.route.startTime
        );
        nextRoutePlanRoutesEndTimeField.input.onChange(values.route.endTime);
        nextRoutePlanRoutesDriverIdField.input.onChange(values.route.driverId);
        nextRoutePlanRoutesBusAssistantIdField.input.onChange(
          values.route.busAssistantId
        );
        nextRoutePlanRoutesVehicleIdField.input.onChange(
          values.route.vehicleId
        );
        nextRoutePlanRoutesLocationsField.input.onChange(
          values.route.routeLocations
        );
        nextRoutePlanRoutesBackupDriversField.input.onChange(
          values.route.backupDriverIds
        );
        nextRoutePlanRoutesBackupVehiclesField.input.onChange(
          values.route.backupVehicleIds
        );
        nextRoutePlanRoutesBackupBusAssistantsField.input.onChange(
          values.route.backupBusAssistantIds
        );

        routeField.input.onChange({});

        notify(`Successfully update the route.`, "info");
        handleCloseRoutePanel();
      },
      onFailure: (error) => {
        notify(`Failed update the route, error: ${error.message}`, "warning");
      },
    }
  );

  const convertStudentData = (routeLocationData: any) => {
    let locations: any = [];

    routeLocationData.forEach((location: any) => {
      const temp = _.cloneDeep(location.students);
      const studentIds = temp.map((item: any) => {
        return item ? item.id : item;
      });

      location.studentIds = studentIds;

      locations.push(location);
    });

    return { routeLocations: locations };
  };

  useEffect(() => {
    const routeLocations = convertStudentData(routeLocationData);

    routeField.input.onChange({
      ...routeField.input.value,
      ...routeLocations,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocationData]);

  const handleSave = () => {
    if (isUpdateRoutePlan) {
      // send directly to API
      handleUpdateRoute();
    } else {
      routesNameField.input.onChange(values.route.name);
      routesBusBayNumberField.input.onChange(values.route.busBayNumber);
      routesStartTimeField.input.onChange(values.route.startTime);
      routesEndTimeField.input.onChange(values.route.endTime);
      routesDriverIdField.input.onChange(values.route.driverId);
      routesBusAssistantIdField.input.onChange(values.route.busAssistantId);
      routesVehicleIdField.input.onChange(values.route.vehicleId);
      routesLocationsField.input.onChange(values.route.routeLocations);
      routesBackupDriversField.input.onChange(values.route.backupDriverIds);
      routesBackupVehiclesField.input.onChange(values.route.backupVehicleIds);
      routesBackupBusAssistantsField.input.onChange(
        values.route.backupBusAssistantIds
      );

      routeField.input.onChange({});
      handleCloseRoutePanel();
    }
  };

  return (
    <Button
      color="primary"
      onClick={() => handleSave()}
      label={loading ? "Please wait..." : "Confirm"}
      autoFocus
    >
      <SaveIcon />
    </Button>
  );
};

const UpdateRouteButton = ({
  routeLocationData,
  index,
  handleCloseRoutePanel,
  isUpdateRoutePlan,
}: IUpdateRouteButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <Button
        className={classes.sidePanelSaveRouteButton}
        onClick={handleOpenDialog}
      >
        <SaveIcon />
      </Button>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Update Route</DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            Are you sure to update route? this action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <CustomConfirmButton
            routeLocationData={routeLocationData}
            index={index}
            handleCloseRoutePanel={handleCloseRoutePanel}
            isUpdateRoutePlan={isUpdateRoutePlan}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default UpdateRouteButton;
